import type { ReactNode } from "react";
import type { Profile, View } from "../../types";
import type { UpcomingAlert } from "../../utils/assetDates";
import NotificationCenter from "../notifications/NotificationCenter";
import "./AppShell.css";

type Props = {
  view: View;
  profile: Profile;
  counts: { raft: number; boat: number };
  alerts: UpcomingAlert[];
  onNavigate: (view: View) => void;
  onOpenAlert: (alert: UpcomingAlert) => void;
  onLogout: () => void;
  children: ReactNode;
};

const TITLES: Record<View, string> = {
  Dashboard: "Asset Overview",
  "Life Raft": "Life Raft",
  "Life Boat": "Life Boat",
  Users: "User Management",
  Settings: "Settings",
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export default function AppShell({
  view,
  profile,
  counts,
  alerts,
  onNavigate,
  onOpenAlert,
  onLogout,
  children,
}: Props) {
  const navButton = (target: View, icon: string, count?: number) => (
    <button
      type="button"
      className={view === target ? "active" : ""}
      onClick={() => onNavigate(target)}
    >
      <span aria-hidden="true">{icon}</span>
      {target}
      {typeof count === "number" && <b>{count}</b>}
    </button>
  );

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">IX</div>
          <div>
            <strong>INSCAPE</strong>
            <span>SeaScape Asset Control</span>
          </div>
        </div>

        <nav aria-label="Primary navigation">
          {navButton("Dashboard", "⌂")}
          <p>ASSET MODULES</p>
          {navButton("Life Raft", "◫", counts.raft)}
          {navButton("Life Boat", "◇", counts.boat)}
          {profile.role === "Admin" && (
            <>
              <p>ADMINISTRATION</p>
              {navButton("Users", "♙")}
            </>
          )}
          <p>ACCOUNT</p>
          {navButton("Settings", "⚙")}
          <button type="button" onClick={onLogout}>
            <span aria-hidden="true">↪</span>
            Logout
          </button>
        </nav>

        <div className="sidebar-foot">
          <span className="live-dot" /> Demo environment
          <br />
          <small>Database connected</small>
        </div>
      </aside>

      <main className="main-content">
        <header className="app-header">
          <div>
            <p className="eyebrow">MARINE SAFETY OPERATIONS</p>
            <h1>{TITLES[view]}</h1>
          </div>
          <div className="header-actions">
            <NotificationCenter alerts={alerts} onOpen={onOpenAlert} />
            <div className="secure-badge">● Authenticated</div>
            <div className="avatar">{initials(profile.fullName) || "SS"}</div>
            <div className="account">
              <strong>{profile.fullName}</strong>
              <span>{profile.role}</span>
            </div>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
}
